/**
 * The dealer workspace's Texts slice of the data provider (DataProvider.texts), over the texting routes (switch texting
 * on the store; the API answers 404 feature_disabled while it is off). Live, and a fixture with sample threads of a
 * sample dealership (clearly labelled "Sample"): in fixture mode nothing is ever sent, a sent message is stored as
 * "Not sent (test mode)".
 *   /v1/app/texting/status · /threads* · /quick-replies* · /settings · /registration*   /v1/app/files (photo upload)
 */
import { APP, ApiError, request, type Http } from '@app/core/http';

export type UUID = string;
export type ISO = string;

export interface TextStatus {
  texts_live: boolean;
  test_mode: boolean;
  paused: boolean;
  sender: string | null;
  one_way: boolean;
  unread: number;
}
export interface ThreadRow {
  id: UUID;
  version: number;
  sender_e164: string;
  customer_e164: string;
  customer_id: UUID | null;
  desk_call_id: UUID | null;
  appointment_id: UUID | null;
  status: 'open' | 'waiting_customer' | 'closed';
  assigned_user_id: UUID | null;
  assigned_name: string | null;
  unread_count: number;
  last_message_at: ISO | null;
  last_inbound_at: ISO | null;
  last_preview: string | null;
  flag: 'possible_optout' | null;
  one_way: boolean;
  customer_name: string | null;
  customer_first_name: string | null;
  preferred_language: 'en' | 'es' | null;
}
export interface TextMedia {
  id: UUID;
  file_id: UUID | null;
  content_type: string | null;
  status: 'pending' | 'stored' | 'rejected';
  reason: string | null;
}
export interface TextMessage {
  id: UUID;
  thread_id: UUID;
  direction: 'inbound' | 'outbound';
  author_kind: 'customer' | 'staff' | 'ai' | 'system' | null;
  author_name: string | null;
  body: string | null;
  template: string | null;
  status: string;
  tick?: string;
  error_text?: string | null;
  segments: number | null;
  send_after: ISO | null;
  sent_at: ISO | null;
  delivered_at: ISO | null;
  occurred_at: ISO;
  keyword: string | null;
  media: TextMedia[];
}
export interface ThreadDetail extends ThreadRow {
  messages: TextMessage[];
  more_before: ISO | null;
  consent: { allowed: boolean; reason: string; last: { at: ISO; source: string; consent: boolean } | null };
  unreachable: { at: ISO; text: string } | null;
  draft: { id: UUID; body?: string; created_at?: ISO; pending?: boolean } | null;
  gate: { test_mode: boolean; paused: boolean; store_on: boolean };
}
export interface QuickReply {
  id: UUID;
  title: string;
  body: string;
  sort: number;
}
export interface TextSettings {
  ai_mode: 'draft' | 'after_hours' | 'always';
  auto_assign: 'none' | 'confirmer' | 'round_robin';
  request_received_text: boolean;
  reminder_24h: boolean;
  reminder_2h: boolean;
  link_allowlist: string[];
  version: number;
}
export interface Registration {
  numbers: {
    e164: string;
    purpose: string;
    sms_enabled: boolean;
    program: { kind?: string; status: string | null; status_text: string; approved_at?: ISO | null };
  }[];
  brand: {
    legal_name: string | null;
    ein_last4: string | null;
    address: Record<string, string | null>;
    website: string | null;
    contact: Record<string, string | null>;
    submitted_at: ISO | null;
  } | null;
}
export interface BrandForm {
  legal_name: string;
  ein?: string;
  address?: { line1?: string; line2?: string; city?: string; state?: string; zip?: string };
  website?: string;
  contact?: { name?: string; email?: string; phone?: string };
}
export interface SendBody {
  client_key: string;
  body?: string;
  quick_reply_id?: UUID;
  template?: string;
  vars?: Record<string, string>;
  file_ids?: UUID[];
  draft_id?: UUID;
}
export interface SendResult {
  status: string;
  message: TextMessage | null;
  replayed?: boolean;
}

export interface Api {
  status(): Promise<TextStatus>;
  threads(q: { filter?: string; q?: string; cursor?: string | null }): Promise<{ items: ThreadRow[]; next: string | null }>;
  thread(id: UUID): Promise<ThreadDetail>;
  start(body: {
    customer_id?: UUID;
    phone?: string;
    client_key: string;
    body?: string;
    template?: string;
    vars?: Record<string, string>;
  }): Promise<SendResult>;
  send(id: UUID, body: SendBody): Promise<SendResult>;
  assign(id: UUID, userId: UUID | null, version?: number): Promise<ThreadRow>;
  action(id: UUID, action: 'read' | 'close' | 'reopen'): Promise<ThreadRow>;
  optout(id: UUID, decision: 'opted_out' | 'not_optout'): Promise<ThreadRow>;
  requestDraft(id: UUID): Promise<{ id: UUID; status: string }>;
  discardDraft(id: UUID, draftId: UUID): Promise<{ discarded: boolean }>;
  quickReplies(): Promise<QuickReply[]>;
  createQuickReply(body: { title: string; body: string; sort?: number }): Promise<QuickReply>;
  updateQuickReply(id: UUID, body: { title: string; body: string; sort?: number }): Promise<QuickReply>;
  archiveQuickReply(id: UUID): Promise<{ archived: boolean }>;
  settings(): Promise<TextSettings>;
  saveSettings(body: Partial<Omit<TextSettings, 'version'>> & { version: number }): Promise<TextSettings>;
  registration(): Promise<Registration>;
  saveBrand(body: BrandForm): Promise<Registration['brand']>;
  uploadPhoto(file: Blob, customerId: UUID | null): Promise<{ id: UUID }>;
}

const X = `${APP}/texting`;
const e = encodeURIComponent;

export const live = (http: Http): Api => ({
  status: () => http.get(`${X}/status`),
  threads: (q) => http.get(`${X}/threads`, { query: { filter: q.filter, q: q.q, cursor: q.cursor ?? undefined } }),
  thread: (id) => http.get<{ thread: ThreadDetail }>(`${X}/threads/${e(id)}`).then((r) => r.thread),
  start: (body) => http.post(`${X}/threads`, body),
  send: (id, body) => http.post(`${X}/threads/${e(id)}/messages`, body),
  assign: (id, userId, version) =>
    http
      .post<{ thread: ThreadRow }>(`${X}/threads/${e(id)}/assign`, { user_id: userId, ...(version != null ? { version } : {}) })
      .then((r) => r.thread),
  action: (id, action) => http.post<{ thread: ThreadRow }>(`${X}/threads/${e(id)}/${action}`, {}).then((r) => r.thread),
  optout: (id, decision) => http.post<{ thread: ThreadRow }>(`${X}/threads/${e(id)}/optout`, { decision }).then((r) => r.thread),
  requestDraft: (id) => http.post<{ draft: { id: UUID; status: string } }>(`${X}/threads/${e(id)}/ai-draft`, {}).then((r) => r.draft),
  discardDraft: (id, draftId) => http.post(`${X}/threads/${e(id)}/drafts/${e(draftId)}/discard`, {}),
  quickReplies: () => http.get<{ items: QuickReply[] }>(`${X}/quick-replies`).then((r) => r.items),
  createQuickReply: (body) => http.post<{ item: QuickReply }>(`${X}/quick-replies`, body).then((r) => r.item),
  updateQuickReply: (id, body) => http.put<{ item: QuickReply }>(`${X}/quick-replies/${e(id)}`, body).then((r) => r.item),
  archiveQuickReply: (id) => http.del(`${X}/quick-replies/${e(id)}`),
  settings: () => http.get<{ settings: TextSettings }>(`${X}/settings`).then((r) => r.settings),
  saveSettings: (body) => http.put<{ settings: TextSettings }>(`${X}/settings`, body).then((r) => r.settings),
  registration: () => http.get(`${X}/registration`),
  saveBrand: (body) => http.put<{ brand: Registration['brand'] }>(`${X}/registration/brand`, body).then((r) => r.brand),
  uploadPhoto: (file, customerId) => {
    const f = new FormData();
    f.set('kind', 'photo');
    if (customerId) {
      f.set('owner_type', 'customer');
      f.set('owner_id', customerId);
    }
    f.set('file', file, 'photo');
    return request<{ file: { id: UUID } }>('POST', `${APP}/files`, { body: f }).then((r) => r.file);
  },
});

// ── fixture: sample threads of a sample dealership; nothing is ever sent ─────────────────────────────────────────────
const SAMPLE_SENDER = '+15105550100';
const ago = (min: number) => new Date(Date.now() - min * 60_000).toISOString();
let n = 0;
const fid = (p: string) => `${p}-0000-7000-8000-${String((n += 1)).padStart(12, '0')}`;

function sampleDb() {
  const mk = (over: Partial<ThreadRow>): ThreadRow => ({
    id: fid('0190f6d2-7e57'),
    version: 1,
    sender_e164: SAMPLE_SENDER,
    customer_e164: '+15105550142',
    customer_id: null,
    desk_call_id: null,
    appointment_id: null,
    status: 'open',
    assigned_user_id: null,
    assigned_name: null,
    unread_count: 0,
    last_message_at: ago(5),
    last_inbound_at: ago(5),
    last_preview: null,
    flag: null,
    one_way: false,
    customer_name: null,
    customer_first_name: null,
    preferred_language: 'en',
    ...over,
  });
  const msg = (thread: ThreadRow, over: Partial<TextMessage>): TextMessage => ({
    id: fid('0190f6d2-3e55'),
    thread_id: thread.id,
    direction: 'outbound',
    author_kind: 'system',
    author_name: null,
    body: '',
    template: null,
    status: 'held_test_mode',
    segments: 1,
    send_after: null,
    sent_at: null,
    delivered_at: null,
    occurred_at: ago(60),
    keyword: null,
    media: [],
    ...over,
  });
  const a = mk({
    customer_name: 'Sample: Dana Ruiz',
    customer_first_name: 'Dana',
    unread_count: 1,
    last_preview: 'Can I come Friday instead?',
    customer_e164: '+15105550142',
  });
  const b = mk({
    customer_name: 'Sample: Marco Silva',
    customer_first_name: 'Marco',
    status: 'waiting_customer',
    assigned_name: 'Priya N.',
    last_message_at: ago(140),
    last_inbound_at: ago(150),
    last_preview: 'Your vehicle is ready for pickup.',
    customer_e164: '+15105550177',
  });
  const c = mk({
    customer_name: 'Sample: Ana Ortiz',
    customer_first_name: 'Ana',
    flag: 'possible_optout',
    last_message_at: ago(600),
    last_inbound_at: ago(600),
    last_preview: 'Wrong number I think',
    customer_e164: '+15105550188',
  });
  const threads = [a, b, c];
  const messages = new Map<UUID, TextMessage[]>([
    [
      a.id,
      [
        msg(a, {
          body: 'Sample Honda: Hi Dana, your service visit is confirmed for Thu Oct 23, 9:00a PDT. Reply here or call (510) 555-0100 to change it.',
          template: 'service_visit_confirmed',
          occurred_at: ago(90),
        }),
        msg(a, {
          direction: 'inbound',
          author_kind: 'customer',
          body: 'Can I come Friday instead?',
          status: 'received',
          occurred_at: ago(5),
        }),
      ],
    ],
    [
      b.id,
      [
        msg(b, { direction: 'inbound', author_kind: 'customer', body: 'Is my car done yet?', status: 'received', occurred_at: ago(150) }),
        msg(b, { author_kind: 'staff', author_name: 'Priya Nair', body: 'Your vehicle is ready for pickup.', occurred_at: ago(140) }),
      ],
    ],
    [
      c.id,
      [msg(c, { direction: 'inbound', author_kind: 'customer', body: 'Wrong number I think', status: 'received', occurred_at: ago(600) })],
    ],
  ]);
  const quick: QuickReply[] = [
    { id: fid('0190f6d2-9a11'), title: 'Our hours', body: 'We open at {opens}.', sort: 0 },
    { id: fid('0190f6d2-9a11'), title: 'Photo of the light', body: 'Can you send a photo of the warning light?', sort: 1 },
    { id: fid('0190f6d2-9a11'), title: 'Ready for pickup', body: 'Your vehicle is ready for pickup.', sort: 2 },
  ];
  let settings: TextSettings = {
    ai_mode: 'draft',
    auto_assign: 'none',
    request_received_text: true,
    reminder_24h: true,
    reminder_2h: false,
    link_allowlist: [],
    version: 1,
  };
  return {
    threads,
    messages,
    quick,
    get settings() {
      return settings;
    },
    set settings(s) {
      settings = s;
    },
  };
}

export const fixture = (): Api => {
  const db = sampleDb();
  const notFound = () => Promise.reject(new ApiError(404, 'not_found', null, { ok: false, error: 'not_found' }));
  const find = (id: UUID) => db.threads.find((t) => t.id === id);
  const detail = (t: ThreadRow): ThreadDetail => ({
    ...t,
    messages: db.messages.get(t.id) ?? [],
    more_before: null,
    consent: { allowed: true, reason: 'ok', last: { at: ago(2000), source: 'call_ai', consent: true } },
    unreachable: null,
    draft: null,
    gate: { test_mode: true, paused: false, store_on: true },
  });
  const append = (t: ThreadRow, body: string): SendResult => {
    const m: TextMessage = {
      id: fid('0190f6d2-3e55'),
      thread_id: t.id,
      direction: 'outbound',
      author_kind: 'staff',
      author_name: 'You',
      body,
      template: null,
      status: 'held_test_mode',
      segments: 1,
      send_after: null,
      sent_at: null,
      delivered_at: null,
      occurred_at: new Date().toISOString(),
      keyword: null,
      media: [],
    };
    db.messages.set(t.id, [...(db.messages.get(t.id) ?? []), m]);
    Object.assign(t, { unread_count: 0, last_preview: body.slice(0, 120), last_message_at: m.occurred_at, status: 'waiting_customer' });
    return { status: m.status, message: m };
  };
  const filtered = (f?: string) =>
    db.threads.filter((t) =>
      f === 'unread'
        ? t.unread_count > 0
        : f === 'unassigned'
          ? !t.assigned_name
          : f === 'flagged'
            ? !!t.flag
            : f === 'closed'
              ? t.status === 'closed'
              : f === 'mine'
                ? false
                : t.status !== 'closed',
    );
  return {
    status: async () => ({
      texts_live: false,
      test_mode: true,
      paused: false,
      sender: SAMPLE_SENDER,
      one_way: false,
      unread: db.threads.filter((t) => t.unread_count > 0).length,
    }),
    threads: async (q) => ({
      items: filtered(q.filter).filter((t) => !q.q || (t.customer_name ?? '').toLowerCase().includes(q.q.toLowerCase())),
      next: null,
    }),
    thread: async (id) => (find(id) ? detail(find(id)!) : notFound()),
    start: async () => ({ status: 'held_test_mode', message: null }),
    send: async (id, body) => {
      const t = find(id);
      if (!t) return notFound();
      const q = body.quick_reply_id ? db.quick.find((x) => x.id === body.quick_reply_id)?.body : null;
      return append(t, body.body ?? q ?? (body.template ? `(${body.template.replace(/_/g, ' ')})` : ''));
    },
    assign: async (id, userId) => Object.assign(find(id)!, { assigned_user_id: userId, assigned_name: userId ? 'Assigned' : null }),
    action: async (id, action) =>
      Object.assign(find(id)!, action === 'read' ? { unread_count: 0 } : { status: action === 'close' ? 'closed' : 'open' }),
    optout: async (id) => Object.assign(find(id)!, { flag: null }),
    requestDraft: () => Promise.reject(new ApiError(404, 'feature_disabled', null, { ok: false, error: 'feature_disabled' })),
    discardDraft: async () => ({ discarded: true }),
    quickReplies: async () => db.quick,
    createQuickReply: async (b) => {
      const q = { id: fid('0190f6d2-9a11'), sort: db.quick.length, ...b };
      db.quick.push(q);
      return q;
    },
    updateQuickReply: async (id, b) =>
      Object.assign(
        db.quick.find((q) => q.id === id)!,
        b,
      ),
    archiveQuickReply: async (id) => {
      db.quick.splice(
        db.quick.findIndex((q) => q.id === id),
        1,
      );
      return { archived: true };
    },
    settings: async () => db.settings,
    saveSettings: async ({ version: _v, ...b }) => (db.settings = { ...db.settings, ...b, version: db.settings.version + 1 }),
    registration: async () => ({
      numbers: [{ e164: SAMPLE_SENDER, purpose: 'ai', sms_enabled: false, program: { status: null, status_text: 'Not filed yet' } }],
      brand: null,
    }),
    saveBrand: async (b) => ({
      legal_name: b.legal_name,
      ein_last4: b.ein ? b.ein.replace(/\D/g, '').slice(-4) : null,
      address: b.address ?? {},
      website: b.website ?? null,
      contact: b.contact ?? {},
      submitted_at: new Date().toISOString(),
    }),
    uploadPhoto: () => Promise.reject(new ApiError(400, 'sample_store', null, { ok: false, error: 'sample_store' })),
  };
};
